import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import InternationalSEO from '../../components/seo/InternationalSEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    travelType: 'personal'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d356q82s93udigdt8m10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          travelType: 'personal'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <InternationalSEO 
        page="contact"
        title="Contact MAVESKY TRAVELS | 24/7 Customer Support | Houston Travel Agency"
        description="Contact MAVESKY TRAVELS for professional flight booking assistance. 24/7 customer support available at (281) 235-6029. Located in Houston, Texas. Expert travel consultation and booking services worldwide. Get personalized help with your travel needs."
        keywords="contact MAVESKY TRAVELS, travel agency contact, flight booking support, customer service, Houston travel agency, 24/7 support, travel consultation, professional travel help, flight booking assistance, travel agent contact"
        canonicalUrl="https://maveskytravels.com/contact"
      />
      
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Contact <strong>Us</strong>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to book your next adventure? Get in touch with our <strong>travel experts</strong> for 
              <strong>personalized assistance</strong> and <strong>professional flight booking</strong> services.
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-white mb-8">Get In Touch</h2>
                
                <div className="space-y-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl mr-4">
                      <i className="ri-phone-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Phone Support</h3>
                      <p className="text-gray-400">(281) 235-6029</p>
                      <p className="text-gray-400">Available for bookings</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl mr-4">
                      <i className="ri-mail-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Email Support</h3>
                      <p className="text-gray-400">EHAS123@AOL.COM</p>
                      <p className="text-gray-400">Response within 2 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl mr-4">
                      <i className="ri-map-pin-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Office Location</h3>
                      <p className="text-gray-400">9898 Bissonnet Street</p>
                      <p className="text-gray-400">Houston, Texas 77036</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-xl mr-4">
                      <i className="ri-time-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Business Hours</h3>
                      <p className="text-gray-400">Monday - Friday: 6:00 AM - 10:00 PM CST</p>
                      <p className="text-gray-400">Weekend: 8:00 AM - 8:00 PM CST</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-white font-semibold mb-4">Why Choose MAVESKY?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                      Global flight booking expertise
                    </li>
                    <li className="flex items-center text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                      Transparent 1% agency fee
                    </li>
                    <li className="flex items-center text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                      Professional customer support
                    </li>
                    <li className="flex items-center text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-check-line text-green-400"></i>
                      </div>
                      Daily price updates
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-white font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-white font-medium mb-2">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-white font-medium mb-2">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                        placeholder="(281) 235-6029"
                      />
                    </div>
                    <div>
                      <label htmlFor="travelType" className="block text-white font-medium mb-2">Travel Type</label>
                      <select
                        id="travelType"
                        name="travelType"
                        value={formData.travelType}
                        onChange={handleInputChange}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 pr-8"
                      >
                        <option value="personal">Personal Travel</option>
                        <option value="business">Business Travel</option>
                        <option value="group">Group Travel</option>
                        <option value="corporate">Corporate Account</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-white font-medium mb-2">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 resize-none"
                      placeholder="Tell us about your travel needs..."
                    ></textarea>
                    <div className="text-right text-sm text-gray-400 mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-7

                    disabled:bg-gray-600 text-white py-3 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-900/30 border border-green-800 rounded-lg p-4 text-green-300">
                      Thank you! Your message has been sent successfully. We'll get back to you within 2 hours.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-red-300">
                      Sorry, there was an error sending your message. Please try again or call us directly.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
